import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';
import { defineMessages, injectIntl, IntlShape } from 'react-intl';
import { List } from 'antd';
import { ArrowRightOutlined, CheckCircleTwoTone } from '@ant-design/icons';

import Connector, { PropsFromRedux } from './Connector';
import {
  FacilityMappingStrategyState,
  FacilityType,
} from 'src/routes/Customers/components/OperatorsTab/modules/types';
import ActionButtons from './components/ActionButtons';
import {
  FacilityLabel,
  FacilityMappingWrap,
  FacilitySearchBox,
  FacilitySearchBoxAndButtonWrap,
  FacilityTenantFieldsRow,
} from './style';
import { UUID } from 'src/types/utility';
import { DATA_STRATEGY, DEFAULT_PAGINATION } from 'src/utils/constants';
import { ExtraActionBtn } from '../../style';
import ActionButton from 'src/components/general-ui/Buttons/ActionButton';
import { notifyUserByActionTypeAndCode } from 'src/utils/errorHandling/notifications';
import { actions as emrMappingsActions } from 'src/redux/data/emrMappings';
import { tenantsFromFile } from 'src/routes/Customers/components/OperatorsTab/components/OperatorWizard/steps/FacilityMapping/utils';

type Props = PropsFromRedux & {
  intl: IntlShape;
  customerId: UUID;
  emrType: string;
  orgId: string;
  setDisabledNextBtn: Dispatch<SetStateAction<boolean>>;
};

const FacilitiesFieldMapping = ({
  intl,
  getOperatorTenants,
  getFacilities,
  operatorTenantsList,
  siteGroupsList,
  facilitiesList,
  facilityInitialDict,
  isLoadingOperatorTenants,
  isLoadingFacilities,
  isLoadingMappingFacilities,
  getSiteGroups,
  mapFacilities,
  customerId,
  emrType,
  orgId,
  syncFacility,
  isLoadingSyncFacility,
  onboardPatients,
  isLoadingOnboardPatients,
  onboardFacility,
  isLoadingOnboardFacility,
  setDisabledNextBtn,
}: Props) => {
  const [mapFaciltiesDict, setMapFaciltiesDict] =
    useState<FacilityMappingStrategyState>({});
  const [facilitiesData, setFacilitiesData] = useState<FacilityType[] | []>([]);
  const [onboardFacilitiesData, setOnboardFacilitiesData] = useState<
    FacilityType[] | []
  >([]);
  const facilitiesFileInputRef = useRef<HTMLInputElement>(null);
  const [isParsingFile, setIsParsingFile] = useState(false);
  let onboardFacilitiesList: FacilityType[] | [] = [];

  useEffect(() => {
    getOperatorTenants();
    getFacilities();
    getSiteGroups({
      customerID: customerId,
      page: DEFAULT_PAGINATION.PAGE - 1,
      limit: DEFAULT_PAGINATION.LIMIT_10000,
      search: '',
    });
  }, [getOperatorTenants, getFacilities, getSiteGroups, customerId]);

  useEffect(() => {
    if (!isLoadingFacilities) {
      setMapFaciltiesDict(facilityInitialDict);
      setFacilitiesData(facilitiesList);
    }
  }, [isLoadingFacilities, operatorTenantsList]);

  const parseTenantsFile = () => {
    setIsParsingFile(true);
    try {
      const file = facilitiesFileInputRef.current?.files?.[0];
      if (!file) return;
      tenantsFromFile(file, orgId, emrType)
        .then(facilityTypes => {
          onboardFacilitiesList = facilityTypes;
          setOnboardFacilitiesData(onboardFacilitiesList);
        })
        .catch(error => {
          console.log('error parsing facilities file', error);
          notifyUserByActionTypeAndCode(
            emrMappingsActions.parseTenantFile.type,
            file,
            error,
          );
        });
    } finally {
      setIsParsingFile(false);
    }
  };

  // Combine and sort facilitiesData and onboardFacilitiesData together
  const combinedFacilities = [
    ...facilitiesData.filter(
      value =>
        onboardFacilitiesData.filter(item => item.id === value.id).length === 0,
    ),
    ...onboardFacilitiesData,
  ].sort((a, b) => a.facilityName.localeCompare(b.facilityName));

  const combinedFacilitiesListData = combinedFacilities.map(facility => {
    const { id, facilityName, organizationId } = facility;
    const isOnboardFacility = onboardFacilitiesData.some(f => f.id === id);
    return (
      <FacilityTenantFieldsRow key={id}>
        <FacilityLabel
          width="7rem"
          isLinked={mapFaciltiesDict[id]?.strategy === DATA_STRATEGY.LINKED}
        >
          {facilityName}
        </FacilityLabel>
        {mapFaciltiesDict[id]?.strategy === DATA_STRATEGY.LINKED ? (
          <CheckCircleTwoTone twoToneColor="#52c41a" />
        ) : (
          <ArrowRightOutlined />
        )}
        <ActionButtons
          emrType={emrType}
          facilityId={id}
          organizationId={organizationId}
          mapFaciltiesDict={mapFaciltiesDict}
          setMapFaciltiesDict={setMapFaciltiesDict}
          operatorTenantsList={operatorTenantsList}
          siteGroupsList={siteGroupsList}
          syncFacility={syncFacility}
          isLoadingSyncFacility={isLoadingSyncFacility}
          setDisabledNextBtn={setDisabledNextBtn}
          onboardPatients={onboardPatients}
          isLoadingOnboardPatients={isLoadingOnboardPatients}
          onboardFacility={onboardFacility}
          isLoadingOnboardFacility={isLoadingOnboardFacility}
          isOnboardFacility={isOnboardFacility}
          facilitiesFileInputRef={facilitiesFileInputRef}
        />
      </FacilityTenantFieldsRow>
    );
  });

  const onSearch = (value: string) => {
    const searchValue = value.toLowerCase();

    if (!searchValue) {
      setFacilitiesData(facilitiesList);
      setOnboardFacilitiesData(onboardFacilitiesList);
    }

    const facilitySearchResults = facilitiesList.filter(facility => {
      const facilityNameLowerCased = facility.facilityName.toLowerCase();
      return facilityNameLowerCased.includes(searchValue);
    });

    const onboardFacilitySearchResults = onboardFacilitiesList.filter(
      facility => {
        const facilityNameLowerCased = facility.facilityName.toLowerCase();
        return facilityNameLowerCased.includes(searchValue);
      },
    );

    setFacilitiesData(facilitySearchResults);
    setOnboardFacilitiesData(onboardFacilitySearchResults);
  };

  const isBatch = emrType === 'EPIC' || emrType === 'MEDITECH';

  return (
    <FacilityMappingWrap>
      <FacilitySearchBoxAndButtonWrap>
        <FacilitySearchBox
          placeholder={intl.formatMessage(messages.searchFacility)}
          onSearch={onSearch}
        />
        {isBatch && (
          <>
            <ActionButton
              isLoading={isParsingFile}
              className="strategy-button"
              onClick={() => facilitiesFileInputRef.current?.click()}
            >
              {intl.formatMessage(messages.importFacilities)}
            </ActionButton>
            <input
              type="file"
              ref={facilitiesFileInputRef}
              style={{ display: 'none' }}
              accept=".csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={e => {
                const file = e.target.files?.[0];
                file && parseTenantsFile();
              }}
            />
          </>
        )}
      </FacilitySearchBoxAndButtonWrap>
      <List
        dataSource={combinedFacilitiesListData}
        renderItem={item => <List.Item>{item}</List.Item>}
        size="large"
        loading={
          isLoadingOperatorTenants ||
          isLoadingFacilities ||
          isLoadingMappingFacilities
        }
        bordered
      />
      <ExtraActionBtn
        className="extra-action-btn"
        onClick={() => {
          setDisabledNextBtn(false);
          mapFacilities({
            data: mapFaciltiesDict,
            file: facilitiesFileInputRef.current?.files?.[0] || null,
          });
        }}
        isLoading={isLoadingMappingFacilities}
      >
        {intl.formatMessage(messages.mapFacilities)}
      </ExtraActionBtn>
    </FacilityMappingWrap>
  );
};

const messages = defineMessages({
  searchFacility: {
    id: 'FacilityMapping.index.searchFacility',
    defaultMessage: 'Search facility',
  },
  mapFacilities: {
    id: 'FacilityMapping.index.mapFacilities',
    defaultMessage: 'Map facilities',
  },
  importFacilities: {
    id: 'FacilityMapping.index.importFacilities',
    defaultMessage: 'Import Facilities',
  },
});

export default Connector(injectIntl(FacilitiesFieldMapping));
